import React, { Suspense, useEffect, useState } from "react";

import RequireAuth from "./auth/RequireAuth.jsx";

import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import NewHome from "./pages/NewHome.jsx";
import ScrollToTop from "./components/ScrollToTop.jsx";
import PublicJumpToHomeButton from "./components/PublicJumpToHomeButton.jsx";
import FullScreenLoader from "./components/FullScreenLoader.jsx";

const PublicVote = React.lazy(() => import("./pages/PublicVote.jsx"));
const NewAssociations = React.lazy(() => import("./pages/NewAssociations.jsx"));
const RentalsPublicPage = React.lazy(
  () => import("./pages/RentalsPublicPage.jsx"),
);
const Evenements = React.lazy(
  () => import("./pages/evenements/Evenements.jsx"),
);
const EvenementDetail = React.lazy(
  () => import("./pages/evenements/EvenementDetail.jsx"),
);
const Connexion = React.lazy(() => import("./pages/auth/Connexion.jsx"));
const AdminConnexion = React.lazy(
  () => import("./pages/auth/AdminConnexion.jsx"),
);
const TwoFactorVerify = React.lazy(
  () => import("./pages/auth/TwoFactorVerify.jsx"),
);
const TwoFactorSetup = React.lazy(
  () => import("./pages/auth/TwoFactorSetup.jsx"),
);
const MotDePasseOublie = React.lazy(
  () => import("./pages/auth/MotDePasseOublie.jsx"),
);
const ResetPassword = React.lazy(
  () => import("./pages/auth/ResetPassword.jsx"),
);
const Contact = React.lazy(() => import("./pages/Contact.jsx"));
const DevenirPartenaire = React.lazy(
  () => import("./pages/DevenirPartenaire.jsx"),
);
const PolitiqueConfidentialite = React.lazy(
  () => import("./pages/PolitiqueConfidentialite.jsx"),
);
const AccountDeletion = React.lazy(() => import("./pages/AccountDeletion.jsx"));
const NewBusSchedules = React.lazy(() => import("./pages/NewBusSchedules.jsx"));
const PrayerTimesPage = React.lazy(() => import("./pages/PrayerTimesPage.jsx"));
const NotFound = React.lazy(() => import("./pages/NotFound/NotFound.jsx"));
const Forbidden = React.lazy(() => import("./pages/Forbidden/Forbidden.jsx"));
const Unauthorized = React.lazy(
  () => import("./pages/Unauthorized/Unauthorized.jsx"),
);
const AssociationDetail = React.lazy(
  () => import("./pages/AssociationDetail.jsx"),
);

const PublicLayout = React.lazy(() => import("./layouts/PublicLayout.jsx"));
const MerchantDashboardLayout = React.lazy(
  () => import("./layouts/MerchantDashboardLayout.jsx"),
);
const AssociationDashboardLayout = React.lazy(
  () => import("./layouts/AssociationDashboardLayout.jsx"),
);
const CommerceDashboardLayout = React.lazy(
  () => import("./layouts/CommerceDashboardLayout.jsx"),
);
const CommerceRegister = React.lazy(
  () => import("./pages/commerce/CommerceRegister.jsx"),
);
const CommerceDashboard = React.lazy(
  () => import("./pages/commerce/CommerceDashboard.jsx"),
);
const CommerceTreasury = React.lazy(
  () => import("./pages/commerce/CommerceTreasury.jsx"),
);
const CommerceLoyalty = React.lazy(
  () => import("./pages/commerce/CommerceLoyalty.jsx"),
);
const LoyaltyScan = React.lazy(() => import("./pages/LoyaltyScan.jsx"));
const CommerceSubscription = React.lazy(
  () => import("./pages/commerce/CommerceSubscription.jsx"),
);
const CommerceProfile = React.lazy(
  () => import("./pages/commerce/CommerceProfile.jsx"),
);
const CommerceMenuPage = React.lazy(
  () => import("./pages/commerce/CommerceMenuPage.jsx"),
);
const CommercePublications = React.lazy(
  () => import("./pages/commerce/CommercePublications.jsx"),
);
const CommerceGallery = React.lazy(
  () => import("./pages/commerce/CommerceGallery.jsx"),
);
const CommerceReviews = React.lazy(
  () => import("./pages/commerce/CommerceReviews.jsx"),
);
const CommerceNotifications = React.lazy(
  () => import("./pages/commerce/CommerceNotifications.jsx"),
);
const CommerceNotificationCampaigns = React.lazy(
  () => import("./pages/commerce/CommerceNotificationCampaigns.jsx"),
);
const CommerceArticleSearches = React.lazy(
  () => import("./pages/commerce/CommerceArticleSearches.jsx"),
);
const CommercePricing = React.lazy(
  () => import("./pages/commerce/CommercePricing.jsx"),
);
const PharmaciesPublicPage = React.lazy(() =>
  import("./pages/PublicInfoPages.jsx").then((module) => ({
    default: module.PharmaciesPage,
  })),
);
const WeatherPublicPage = React.lazy(() =>
  import("./pages/PublicInfoPages.jsx").then((module) => ({
    default: module.WeatherPage,
  })),
);
const CommercesPublicPage = React.lazy(() =>
  import("./pages/PublicInfoPages.jsx").then((module) => ({
    default: module.CommercesPage,
  })),
);
const CommerceDetailPublicPage = React.lazy(() =>
  import("./pages/PublicInfoPages.jsx").then((module) => ({
    default: module.CommerceDetailPage,
  })),
);
const NewsPublicPage = React.lazy(() =>
  import("./pages/PublicInfoPages.jsx").then((module) => ({
    default: module.NewsPage,
  })),
);
const AboutPublicPage = React.lazy(() =>
  import("./pages/PublicInfoPages.jsx").then((module) => ({
    default: module.AboutPage,
  })),
);

const MerchantDashboard = React.lazy(
  () => import("./pages/merchant/MerchantDashboard.jsx"),
);
const MerchantUsers = React.lazy(
  () => import("./pages/merchant/MerchantUsers.jsx"),
);
const MerchantAssociations = React.lazy(
  () => import("./pages/merchant/MerchantAssociations.jsx"),
);
const MerchantAssociationDetail = React.lazy(
  () => import("./pages/merchant/MerchantAssociationDetail.jsx"),
);
const MerchantAssociationRamadanIftar = React.lazy(
  () => import("./pages/merchant/MerchantAssociationRamadanIftar.jsx"),
);
const MerchantContactMessages = React.lazy(
  () => import("./pages/merchant/MerchantContactMessages.jsx"),
);
const MerchantPartnerRequests = React.lazy(
  () => import("./pages/merchant/MerchantPartnerRequests.jsx"),
);
const MerchantEmailCampaign = React.lazy(
  () => import("./pages/merchant/MerchantEmailCampaign.jsx"),
);
const BloodDonationsPage = React.lazy(() => import('./pages/merchant/BloodDonationsPage.jsx'));
const MerchantNotifications = React.lazy(
  () => import("./pages/merchant/MerchantNotifications.jsx"),
);
const MerchantCommunicationsHub = React.lazy(
  () => import("./pages/merchant/MerchantCommunicationsHub.jsx"),
);
const MerchantPharmaciesPage = React.lazy(
  () => import("./pages/merchant/MerchantPharmaciesPage.jsx"),
);
const MerchantProfile = React.lazy(
  () => import("./pages/merchant/MerchantProfile.jsx"),
);
const MerchantSupport = React.lazy(
  () => import("./pages/merchant/MerchantSupport.jsx"),
);
const MetricsDashboard = React.lazy(
  () => import("./pages/merchant/MetricsDashboard.jsx"),
);
const AuditTrailPage = React.lazy(
  () => import("./pages/merchant/AuditTrailPage.jsx"),
);
const MerchantArticleSearches = React.lazy(
  () => import("./pages/merchant/MerchantArticleSearches.jsx"),
);
const TreasuryDashboard = React.lazy(
  () => import("./pages/merchant/TreasuryDashboard.jsx"),
);
const SuperAdminBusDashboard = React.lazy(
  () => import("./pages/merchant/SuperAdminBusDashboard.jsx"),
);
const RentalsManagementAdmin = React.lazy(
  () => import("./pages/merchant/RentalsManagementAdmin.jsx"),
);
const IdaratDelegationsPage = React.lazy(
  () => import("./pages/merchant/IdaratDelegationsPage.jsx"),
);
const IdaratSeasonsPage = React.lazy(
  () => import("./pages/merchant/IdaratSeasonsPage.jsx"),
);
const CommerceManagementAdmin = React.lazy(
  () => import("./pages/merchant/CommerceManagementAdmin.jsx"),
);
const CommerceReportsAdmin = React.lazy(
  () => import("./pages/merchant/CommerceReportsAdmin.jsx"),
);
const CommerceQuotesAdmin = React.lazy(
  () => import("./pages/merchant/CommerceQuotesAdmin.jsx"),
);
const CommerceSubscriptionsAdmin = React.lazy(
  () => import("./pages/merchant/CommerceSubscriptionsAdmin.jsx"),
);
const CommerceCatalogPage = React.lazy(
  () => import("./pages/merchant/CommerceCatalogPage.jsx"),
);
const InvoicesManagement = React.lazy(
  () => import("./pages/merchant/InvoicesManagement.jsx"),
);
const SubscriptionsManagement = React.lazy(
  () => import("./pages/merchant/SubscriptionsManagement.jsx"),
);

const AssociationDashboard = React.lazy(
  () => import("./pages/association/AssociationDashboard.jsx"),
);
const AssociationInfos = React.lazy(
  () => import("./pages/association/AssociationInfos.jsx"),
);
const AssociationActualites = React.lazy(
  () => import("./pages/association/AssociationActualites.jsx"),
);
const AssociationEvenements = React.lazy(
  () => import("./pages/association/AssociationEvenements.jsx"),
);
const AssociationDons = React.lazy(
  () => import("./pages/association/AssociationDons.jsx"),
);
const AssociationProfil = React.lazy(
  () => import("./pages/association/AssociationProfil.jsx"),
);
const AssociationRamadanIftar = React.lazy(
  () => import("./pages/association/AssociationRamadanIftar.jsx"),
);
const AssociationDocuments = React.lazy(
  () => import("./pages/association/AssociationDocuments.jsx"),
);
const AssociationAnnonces = React.lazy(
  () => import("./pages/association/AssociationAnnonces.jsx"),
);
const AssociationVotes = React.lazy(
  () => import("./pages/association/AssociationVotes.jsx"),
);
const AssociationTirage = React.lazy(
  () => import("./pages/association/AssociationTirage.jsx"),
);

const NotificationsCenter = React.lazy(
  () => import("./pages/NotificationsCenter.jsx"),
);
const DashboardNotificationsRedirect = React.lazy(
  () => import("./pages/DashboardNotificationsRedirect.jsx"),
);
const LoginHistoryPage = React.lazy(
  () => import("./pages/shared/LoginHistoryPage.jsx"),
);
const TechnicalSupportPage = React.lazy(
  () => import("./pages/shared/TechnicalSupportPage.jsx"),
);
const CommerceMessages = React.lazy(
  () => import("./pages/commerce/CommerceMessages.jsx"),
);
const ProvidersPublic = React.lazy(
  () => import("./pages/providers/ProvidersPublic.jsx"),
);
const ProviderDetailPublic = React.lazy(
  () => import("./pages/providers/ProviderDetailPublic.jsx"),
);
const ProviderRegister = React.lazy(
  () => import("./pages/providers/ProviderRegister.jsx"),
);
const ProviderDashboardLayout = React.lazy(
  () => import("./layouts/ProviderDashboardLayout.jsx"),
);
const ProviderDashboard = React.lazy(
  () => import("./pages/providers/ProviderDashboard.jsx"),
);
const ProviderInterventions = React.lazy(
  () => import("./pages/providers/ProviderInterventions.jsx"),
);
const ProviderReviews = React.lazy(
  () => import("./pages/providers/ProviderReviews.jsx"),
);
const ProviderProfile = React.lazy(
  () => import("./pages/providers/ProviderProfile.jsx"),
);
const ProviderSubscription = React.lazy(
  () => import("./pages/providers/ProviderSubscription.jsx"),
);
const ProviderMessages = React.lazy(
  () => import("./pages/providers/ProviderMessages.jsx"),
);
const ProviderManagementAdmin = React.lazy(
  () => import("./pages/merchant/ProviderManagementAdmin.jsx"),
);
const ProviderCatalogPage = React.lazy(
  () => import("./pages/merchant/ProviderCatalogPage.jsx"),
);
const ProviderPaymentsPage = React.lazy(
  () => import("./pages/merchant/ProviderPaymentsPage.jsx"),
);
const ProviderQuoteRequestsPage = React.lazy(
  () => import("./pages/merchant/ProviderQuoteRequestsPage.jsx"),
);
const VisitorCommerceMessages = React.lazy(
  () => import("./pages/VisitorCommerceMessages.jsx"),
);

function PageFallback() {
  return <FullScreenLoader isLoading />;
}

function GlobalPageLoader({ location }) {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    let timer;
    let secondFrame;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        timer = window.setTimeout(() => setLoading(false), 320);
      });
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      if (secondFrame) cancelAnimationFrame(secondFrame);
      if (timer) window.clearTimeout(timer);
    };
  }, [location.pathname, location.search]);

  return <FullScreenLoader isLoading={loading} />;
}

function shouldShowPublicJumpButton(pathname) {
  if (/^\/merchant(?:\/|$)/.test(pathname)) return false;
  if (/^\/association(?:\/|$)/.test(pathname)) return false;
  if (/^\/commerce-dashboard(?:\/|$)/.test(pathname)) return false;
  if (/^\/prestataire-dashboard(?:\/|$)/.test(pathname)) return false;
  if (/^\/dashboard\/superadmin(?:\/|$)/.test(pathname)) return false;
  return true;
}

function App() {
  const location = useLocation();
  const showPublicJumpButton = shouldShowPublicJumpButton(location.pathname);

  return (
    <>
      <ScrollToTop />
      <GlobalPageLoader location={location} />
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/explore" element={<NewHome />} />
          <Route path="/associations" element={<NewAssociations />} />
          <Route path="/locations" element={<RentalsPublicPage />} />
          <Route
            path="/politique-de-confidentialite"
            element={<PolitiqueConfidentialite />}
          />
          <Route path="/suppression-compte" element={<AccountDeletion />} />
          <Route path="/bus-schedules" element={<NewBusSchedules />} />
          <Route path="/pharmacies" element={<PharmaciesPublicPage />} />
          <Route path="/meteo" element={<WeatherPublicPage />} />
          <Route path="/prieres" element={<PrayerTimesPage />} />
          <Route path="/commerces" element={<CommercesPublicPage />} />
          <Route path="/commerces/:id" element={<CommerceDetailPublicPage />} />
          <Route path="/prestataires" element={<ProvidersPublic />} />
          <Route path="/prestataires/:id" element={<ProviderDetailPublic />} />
          <Route path="/actualites" element={<NewsPublicPage />} />
          <Route path="/a-propos" element={<AboutPublicPage />} />
          <Route path="/contact" element={<Contact />} />

          <Route path="/connexion" element={<Connexion />} />
          <Route path="/inscription-commerce" element={<CommerceRegister />} />
          <Route
            path="/inscription-prestataire"
            element={<ProviderRegister />}
          />
          <Route path="/commerces/abonnements" element={<CommercePricing />} />
          <Route
            path="/admin/connexion"
            element={<Navigate to="/admin-connexion" replace />}
          />
          <Route path="/admin-connexion" element={<AdminConnexion />} />
          <Route path="/2fa/verification" element={<TwoFactorVerify />} />
          <Route path="/mot-de-passe-oublie" element={<MotDePasseOublie />} />
          <Route
            path="/reset-password/:uid/:token"
            element={<ResetPassword />}
          />
          <Route path="/associations/:slug" element={<AssociationDetail />} />
          <Route path="/vote/:pollId" element={<PublicVote />} />
          <Route path="/" element={<NewHome />} />
          <Route path="/evenements" element={<Evenements />} />
          <Route path="/evenements/:id" element={<EvenementDetail />} />

          <Route element={<PublicLayout />}>
            <Route path="/annonces" element={<NotificationsCenter />} />
            <Route
              path="/dashboard/notifications"
              element={<DashboardNotificationsRedirect />}
            />

            <Route path="/devenir-partenaire" element={<DevenirPartenaire />} />
            <Route path="/acces-restreint" element={<Unauthorized />} />
            <Route path="/acces-interdit" element={<Forbidden />} />
          </Route>

          <Route element={<RequireAuth allowedRoles={["VISITEUR"]} />}>
            <Route path="/mes-messages" element={<VisitorCommerceMessages />} />
          </Route>

          <Route element={<RequireAuth allowedRoles={["SUPERADMIN"]} />}>
            <Route
              path="/dashboard/superadmin/bus"
              element={<Navigate to="/merchant/bus" replace />}
            />
            <Route path="/merchant" element={<MerchantDashboardLayout />}>
              <Route index element={<MerchantDashboard />} />
              <Route path="bus" element={<SuperAdminBusDashboard />} />
              <Route path="locations" element={<RentalsManagementAdmin />} />
              <Route path="blood-donations" element={<BloodDonationsPage />} />
              <Route path="pharmacies" element={<MerchantPharmaciesPage />} />
              <Route path="delegations" element={<IdaratDelegationsPage />} />
              <Route path="seasons" element={<IdaratSeasonsPage />} />
              <Route
                path="idarat/delegations"
                element={<Navigate to="/merchant/delegations" replace />}
              />
              <Route
                path="idarat/seasons"
                element={<Navigate to="/merchant/seasons" replace />}
              />
              <Route
                path="communications"
                element={<MerchantCommunicationsHub />}
              />
              <Route
                path="notifications"
                element={
                  <Navigate
                    to="/merchant/communications?tab=notifications"
                    replace
                  />
                }
              />
              <Route
                path="notifications-center"
                element={<NotificationsCenter />}
              />
              <Route path="users" element={<MerchantUsers />} />
              <Route path="associations" element={<MerchantAssociations />} />
              <Route
                path="associations/:id"
                element={<MerchantAssociationDetail />}
              />
              <Route
                path="associations/:id/ramadan-iftar"
                element={<MerchantAssociationRamadanIftar />}
              />
              <Route
                path="contact-messages"
                element={
                  <Navigate
                    to="/merchant/communications?tab=messages"
                    replace
                  />
                }
              />
              <Route
                path="partner-requests"
                element={
                  <Navigate
                    to="/merchant/communications?tab=partners"
                    replace
                  />
                }
              />
              <Route
                path="email-campaign"
                element={
                  <Navigate
                    to="/merchant/communications?tab=campaigns"
                    replace
                  />
                }
              />
              <Route path="profil" element={<MerchantProfile />} />
              <Route path="metrics" element={<MetricsDashboard />} />
              <Route path="journal-activite" element={<AuditTrailPage />} />
              <Route
                path="recherches-articles"
                element={<MerchantArticleSearches />}
              />
              <Route path="tresorerie" element={<TreasuryDashboard />} />
              <Route
                path="tresorerie/revenus"
                element={<TreasuryDashboard />}
              />
              <Route
                path="tresorerie/depenses"
                element={<TreasuryDashboard />}
              />
              <Route path="commerces" element={<CommerceManagementAdmin />} />
              <Route
                path="commerces/signalements"
                element={<CommerceReportsAdmin />}
              />
              <Route
                path="commerces/demandes-de-devis"
                element={<CommerceQuotesAdmin />}
              />
              <Route
                path="commerces/demandes-abonnement"
                element={<CommerceSubscriptionsAdmin mode="payments" />}
              />
              <Route
                path="commerces/catalogue"
                element={<CommerceCatalogPage />}
              />
              <Route path="factures" element={<InvoicesManagement />} />
              <Route path="abonnements" element={<SubscriptionsManagement />} />
              <Route
                path="prestataires"
                element={<ProviderManagementAdmin />}
              />
              <Route
                path="prestataires/catalogue"
                element={<ProviderCatalogPage />}
              />
              <Route
                path="prestataires/paiements"
                element={<ProviderPaymentsPage />}
              />
              <Route
                path="prestataires/demandes-abonnement"
                element={<ProviderPaymentsPage />}
              />
              <Route
                path="prestataires/demandes-de-devis"
                element={<ProviderQuoteRequestsPage />}
              />
              <Route
                path="commerce-subscriptions"
                element={<Navigate to="/merchant/commerces" replace />}
              />
              <Route
                path="commerce-configuration"
                element={<Navigate to="/merchant/commerces" replace />}
              />
              <Route
                path="historique-connexion"
                element={<LoginHistoryPage />}
              />
              <Route path="support" element={<MerchantSupport />} />
            </Route>
          </Route>

          <Route element={<RequireAuth allowedRoles={["ASSOCIATION"]} />}>
            <Route path="/association" element={<AssociationDashboardLayout />}>
              <Route index element={<AssociationDashboard />} />
              <Route path="infos" element={<AssociationInfos />} />
              <Route path="profil" element={<AssociationProfil />} />
              <Route path="documents" element={<AssociationDocuments />} />
              <Route path="actualites" element={<AssociationActualites />} />
              <Route path="evenements" element={<AssociationEvenements />} />
              <Route
                path="ramadan-iftar"
                element={<AssociationRamadanIftar />}
              />
              <Route path="dons" element={<AssociationDons />} />
              <Route path="annonces" element={<AssociationAnnonces />} />
              <Route path="votes" element={<AssociationVotes />} />
              <Route path="tirage" element={<AssociationTirage />} />
              <Route path="notifications" element={<NotificationsCenter />} />
              <Route path="securite" element={<TwoFactorSetup />} />
              <Route
                path="support"
                element={<TechnicalSupportPage accountType="association" />}
              />
              <Route
                path="historique-connexion"
                element={<LoginHistoryPage />}
              />
            </Route>
          </Route>

          <Route element={<RequireAuth allowedRoles={["COMMERCANT"]} />}>
            <Route
              path="/commerce-dashboard"
              element={<CommerceDashboardLayout />}
            >
              <Route index element={<CommerceDashboard />} />
              <Route path="abonnement" element={<CommerceSubscription />} />
              <Route path="profil" element={<CommerceProfile />} />
              <Route path="menu" element={<CommerceMenuPage />} />
              <Route path="publications" element={<CommercePublications />} />
              <Route path="galerie" element={<CommerceGallery />} />
              <Route path="avis" element={<CommerceReviews />} />
              <Route path="notifications" element={<CommerceNotifications />} />
              <Route
                path="diffusions"
                element={<CommerceNotificationCampaigns />}
              />
              <Route
                path="recherches-articles"
                element={<CommerceArticleSearches />}
              />
              <Route path="messages" element={<CommerceMessages />} />
              <Route path="tresorerie" element={<CommerceTreasury />} />
              <Route path="fidelite" element={<CommerceLoyalty />} />
              <Route path="support" element={<TechnicalSupportPage />} />
            </Route>
          </Route>

          <Route path="/fidelite/:token" element={<LoyaltyScan />} />

          <Route element={<RequireAuth allowedRoles={["PRESTATAIRE"]} />}>
            <Route
              path="/prestataire-dashboard"
              element={<ProviderDashboardLayout />}
            >
              <Route index element={<ProviderDashboard />} />
              <Route path="interventions" element={<ProviderInterventions />} />
              <Route path="avis" element={<ProviderReviews />} />
              <Route path="profil" element={<ProviderProfile />} />
              <Route path="abonnement" element={<ProviderSubscription />} />
              <Route path="messages" element={<ProviderMessages />} />
              <Route path="notifications" element={<CommerceNotifications />} />
              <Route
                path="support"
                element={<TechnicalSupportPage accountType="provider" />}
              />
            </Route>
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      {showPublicJumpButton ? <PublicJumpToHomeButton /> : null}
    </>
  );
}

export default App;
